<template>
  <div class="behaviour-settings-customized-theme">
    <h2 class="sr-only">
      {{ behaviourTerminology.customizedThemeSectionTitle }}
    </h2>
    <output class="sr-only" aria-live="polite" aria-atomic="true">{{
      liveAnnouncement
    }}</output>
    <Loader :is-processing="state.isLoading || state.isSaving" />
    <div
      class="d-flex flex-wrap justify-content-end gap-2 mb-3"
      data-testid="behaviour-customized-theme-actions"
    >
      <BaseButton
        rounded
        type="button"
        data-testid="behaviour-customized-theme-cancel"
        :is-disabled="state.isLoading || state.isSaving"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </BaseButton>
      <BaseButton
        solid
        rounded
        type="button"
        data-testid="behaviour-customized-theme-save"
        :is-disabled="state.isLoading || state.isSaving || !isDirty"
        @click="onSave"
      >
        {{ $t("behaviour_settings.save") }}
      </BaseButton>
    </div>
    <CAlert
      v-if="state.response.message"
      dismissible
      class="mb-3"
      :color="state.response.type"
      :show="!!state.response.message"
      role="status"
      aria-live="polite"
      :data-testid="`behaviour-customized-theme-${state.response.type}`"
      @close="resetResponse"
    >
      {{ state.response.message }}
    </CAlert>
    <div
      class="behaviour-customized-theme-panel rounded bg-white p-4"
      data-testid="behaviour-customized-theme-panel"
    >
      <section
        class="behaviour-customized-theme-section"
        aria-labelledby="behaviour-customized-theme-heading"
        data-testid="behaviour-customized-theme-section"
      >
        <div
          class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2"
        >
          <h3
            id="behaviour-customized-theme-heading"
            class="behaviour-customized-theme-section__title mb-0"
          >
            {{ behaviourTerminology.renameLabelsTitle }}
          </h3>
          <CustomSwitch
            :model-value="form.renameEnabled"
            :aria-label="
              $t('behaviour_settings.rename_labels_toggle_a11y', {
                section: behaviourTerminology.renameLabelsTitle
              })
            "
            data-testid="behaviour-customized-theme-rename-toggle"
            @update:model-value="onRenameToggle"
          />
        </div>
        <p
          class="behaviour-customized-theme-section__description text-muted mb-4"
        >
          {{ behaviourTerminology.customizedThemeDescription }}
        </p>
        <div
          class="row g-3 behaviour-customized-theme-section__fields"
          :class="{
            'behaviour-customized-theme-section__fields--disabled':
              !form.renameEnabled
          }"
        >
          <div
            v-for="field in labelFields"
            :key="field.key"
            class="col-12 col-md-3"
          >
            <label
              :id="`behaviour-theme-${field.key}-label`"
              class="v3-form-label d-block"
              :class="{ required: form.renameEnabled }"
              :for="`behaviour-theme-${field.key}-input`"
            >
              Rename "{{ field.label }}" to
            </label>
            <InputField
              :id="`behaviour-theme-${field.key}-input`"
              class="d-block w-100"
              :model-value="form.labels[field.key]"
              :placeholder="field.placeholder"
              :disabled="!form.renameEnabled"
              :max-chars="String(maxLabelLength)"
              :max-length="String(maxLabelLength)"
              show-char-counter
              :aria-required="form.renameEnabled"
              :aria-describedby="
                fieldErrors[field.key] ? `${field.key}-error` : undefined
              "
              :invalid-feedback="fieldErrors[field.key]"
              :data-testid="`behaviour-theme-label-${field.key}`"
              @update:model-value="(value) => onLabelChange(field.key, value)"
            />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import behaviourCustomizedThemeConstants from "@/constants/behaviourCustomizedThemeConstants.js"
import {
  cloneCustomizedThemeSettings,
  getDefaultThemeLabels,
  isValidCustomThemeLabel,
  parseCustomizedThemeApiError,
  validateCustomizedThemeSettings
} from "@/helpers/behaviourCustomizedTheme.js"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import { computed, onMounted, reactive, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const { LABEL_KEYS, MAX_LABEL_LENGTH } = behaviourCustomizedThemeConstants

export default {
  name: "BehaviourSettingsCustomizedThemeTab",
  components: {
    BaseButton,
    CustomSwitch,
    InputField,
    Loader
  },
  setup() {
    const { t, locale } = useI18n()
    const store = useStore()
    const liveAnnouncement = ref("")
    const savedSnapshot = ref(null)
    const fieldErrors = reactive({
      [LABEL_KEYS.BEHAVIOUR_MANAGEMENT]: "",
      [LABEL_KEYS.CORE_VALUE]: "",
      [LABEL_KEYS.POSITIVE_BEHAVIOURS]: "",
      [LABEL_KEYS.POINTS]: ""
    })

    const maxLabelLength = MAX_LABEL_LENGTH

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const localeKey = computed(() =>
      String(locale.value || "en")
        .toLowerCase()
        .startsWith("es")
        ? "es"
        : "en"
    )

    const defaultLabels = computed(() => getDefaultThemeLabels(localeKey.value))

    const form = reactive(cloneCustomizedThemeSettings())

    const state = reactive({
      isLoading: false,
      isSaving: false,
      response: {
        type: "",
        message: ""
      }
    })

    const resetResponse = () => {
      state.response = { type: "", message: "" }
    }

    const setSuccessResponse = (message) => {
      state.response = {
        type: "success",
        message: message || "Success!"
      }
    }

    const setErrorResponse = (message) => {
      state.response = {
        type: "danger",
        message: message || t("common.something_went_wrong")
      }
    }

    const labelFields = computed(() => [
      {
        key: LABEL_KEYS.BEHAVIOUR_MANAGEMENT,
        label: defaultLabels.value[LABEL_KEYS.BEHAVIOUR_MANAGEMENT],
        placeholder: defaultLabels.value[LABEL_KEYS.BEHAVIOUR_MANAGEMENT]
      },
      {
        key: LABEL_KEYS.CORE_VALUE,
        label: defaultLabels.value[LABEL_KEYS.CORE_VALUE],
        placeholder: defaultLabels.value[LABEL_KEYS.CORE_VALUE]
      },
      {
        key: LABEL_KEYS.POSITIVE_BEHAVIOURS,
        label: defaultLabels.value[LABEL_KEYS.POSITIVE_BEHAVIOURS],
        placeholder: defaultLabels.value[LABEL_KEYS.POSITIVE_BEHAVIOURS]
      },
      {
        key: LABEL_KEYS.POINTS,
        label: defaultLabels.value[LABEL_KEYS.POINTS],
        placeholder: defaultLabels.value[LABEL_KEYS.POINTS]
      }
    ])

    const isDirty = computed(() => {
      if (!savedSnapshot.value) return false
      return JSON.stringify(form) !== JSON.stringify(savedSnapshot.value)
    })

    const clearFieldErrors = () => {
      Object.keys(fieldErrors).forEach((key) => {
        fieldErrors[key] = ""
      })
    }

    const announce = (message) => {
      liveAnnouncement.value = ""
      if (!message) return
      requestAnimationFrame(() => {
        liveAnnouncement.value = message
      })
    }

    const loadSettings = async () => {
      try {
        state.isLoading = true
        resetResponse()
        const data = await store.dispatch(
          "behaviourSettings/fetchCustomizedThemeSettings"
        )
        Object.assign(form, cloneCustomizedThemeSettings(data))
        savedSnapshot.value = cloneCustomizedThemeSettings(form)
        clearFieldErrors()
      } catch (error) {
        setErrorResponse(
          parseCustomizedThemeApiError(error) ||
            t("behaviour_settings.customized_theme_load_error")
        )
        const defaults = cloneCustomizedThemeSettings({
          renameEnabled: false,
          labels: { ...defaultLabels.value }
        })
        Object.assign(form, defaults)
        savedSnapshot.value = cloneCustomizedThemeSettings(form)
        store.commit(
          "behaviourSettings/SET_BEHAVIOUR_TERMINOLOGY_OVERRIDES",
          null
        )
      } finally {
        state.isLoading = false
      }
    }

    const onRenameToggle = (enabled) => {
      form.renameEnabled = Boolean(enabled)
      if (form.renameEnabled) {
        const bt = behaviourTerminology.value
        form.labels[LABEL_KEYS.BEHAVIOUR_MANAGEMENT] =
          form.labels[LABEL_KEYS.BEHAVIOUR_MANAGEMENT] ||
          bt.behaviourManagement ||
          ""
        form.labels[LABEL_KEYS.CORE_VALUE] =
          form.labels[LABEL_KEYS.CORE_VALUE] || bt.coreValue || ""
        form.labels[LABEL_KEYS.POSITIVE_BEHAVIOURS] =
          form.labels[LABEL_KEYS.POSITIVE_BEHAVIOURS] ||
          bt.positiveBehaviours ||
          ""
        form.labels[LABEL_KEYS.POINTS] =
          form.labels[LABEL_KEYS.POINTS] || bt.points || ""
      }
      clearFieldErrors()
    }

    const onLabelChange = (key, value) => {
      if (!key) return
      form.labels[key] = value ?? ""
      fieldErrors[key] = ""
    }

    const validateForm = () => {
      clearFieldErrors()
      if (!validateCustomizedThemeSettings(form)) {
        Object.values(LABEL_KEYS).forEach((key) => {
          if (!isValidCustomThemeLabel(form.labels[key])) {
            fieldErrors[key] = t(
              "behaviour_settings.theme_label_validation_error"
            )
          }
        })
        return false
      }
      return true
    }

    const onCancel = () => {
      if (state.isLoading || state.isSaving || !savedSnapshot.value) return
      resetResponse()
      Object.assign(form, cloneCustomizedThemeSettings(savedSnapshot.value))
      clearFieldErrors()
      announce(t("behaviour_settings.customized_theme_cancelled"))
    }

    const onSave = async () => {
      if (state.isLoading || state.isSaving) return
      resetResponse()
      if (!validateForm()) {
        const message = t(
          "behaviour_settings.customized_theme_validation_error"
        )
        setErrorResponse(message)
        announce(message)
        return
      }
      try {
        state.isSaving = true
        const data = await store.dispatch(
          "behaviourSettings/upsertCustomizedThemeSettings",
          cloneCustomizedThemeSettings(form)
        )
        Object.assign(form, cloneCustomizedThemeSettings(data))
        savedSnapshot.value = cloneCustomizedThemeSettings(form)
        const savedText = t("behaviour_settings.customized_theme_saved")
        setSuccessResponse(savedText)
        announce(savedText)
      } catch (error) {
        const message =
          parseCustomizedThemeApiError(error) ||
          t("behaviour_settings.customized_theme_save_error")
        setErrorResponse(message)
        announce(message)
      } finally {
        state.isSaving = false
      }
    }

    watch(localeKey, () => {
      loadSettings()
    })

    onMounted(() => {
      loadSettings()
    })

    return {
      behaviourTerminology,
      form,
      state,
      labelFields,
      fieldErrors,
      maxLabelLength,
      isDirty,
      liveAnnouncement,
      onCancel,
      onSave,
      onRenameToggle,
      onLabelChange,
      resetResponse
    }
  }
}
</script>
